import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Spacing, Text } from '@reservamos/elements';
import { trackEvent } from 'user-analytics';
import ModalWithTheme from 'components/ModalWithTheme';
import fingerprintService from 'services/userFingerprint/userFingerprint';
import {
  WELCOME_DIGITAL_DISCOUNT_MODAL_VIEWED,
  WELCOME_DIGITAL_DISCOUNT_MODAL_CLOSED,
  WELCOME_DIGITAL_DISCOUNT_BANNER_SHOWED,
} from 'constants/TrackEvents';
import WelcomeDigitalDiscountMessage from 'ui/atoms/WelcomeDigitalDiscountMessage';
import singleDiscount from '../../../../images/icons/single-discount.webp';
import roundTripDiscount from '../../../../images/icons/rounded-discount.webp';

const trackedBannerScreens = new Set();

/**
 * Renders a discount option card with image and label badge.
 * @param {object} props - imageSrc, badgeClassName, label.
 * @param {string} props.imageSrc - Source URL for the discount icon image.
 * @param {string} props.badgeClassName - Tailwind classes for the badge background (e.g. bg-primary-regular, bg-accent-regular).
 * @param {string} props.label - Text to show in the badge.
 * @returns {JSX.Element} Discount option card.
 */
const DiscountOptionCard = ({ imageSrc, badgeClassName, label }) => (
  <div className="flex flex-col items-center">
    <img src={imageSrc} alt="" className="h-20 object-contain" />
    <div className={`${badgeClassName} text-white px-4 py-2 rounded-2xl`}>
      <p className="text-base font-semibold">{label}</p>
    </div>
  </div>
);

DiscountOptionCard.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  badgeClassName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * WelcomeDigitalDiscountModal component.
 * Renders a clickable banner that opens a modal with discount information.
 * The banner remains visible after closing the modal.
 * @param {object} props - onClose, screen, useTagShapeMobile.
 * @param {function} props.onClose - Callback when the modal is closed.
 * @param {string} props.screen - Screen where the banner is displayed ('results' | 'checkout' | 'passengers').
 * @param {boolean} props.useTagShapeMobile - Pass through to WelcomeDigitalDiscountMessage (clipPath `tagShapeMobile`).
 * @returns {JSX.Element} WelcomeDigitalDiscountModal component.
 */
const WelcomeDigitalDiscountModal = ({ onClose, screen, useTagShapeMobile }) => {
  const { t } = useTranslation('search');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Track banner viewed event when component mounts, deduplicated per screen
  useEffect(() => {
    if (!trackedBannerScreens.has(screen)) {
      trackedBannerScreens.add(screen);
      trackEvent(WELCOME_DIGITAL_DISCOUNT_BANNER_SHOWED, { screen });
    }
  }, [screen]);

  /**
   * Handle opening the modal when user clicks the banner.
   */
  const handleOpen = () => {
    setIsModalOpen(true);
  };

  /**
   * Handle close modal via X button (called by ModalWithTheme onCloseModal).
   */
  const handleCloseByX = () => {
    trackEvent(WELCOME_DIGITAL_DISCOUNT_MODAL_CLOSED, { type: 'icon', screen });
    setIsModalOpen(false);
    onClose?.();
  };

  /**
   * Handle close modal via "Entendido" button.
   */
  const handleCloseByButton = () => {
    trackEvent(WELCOME_DIGITAL_DISCOUNT_MODAL_CLOSED, { type: 'button', screen });
    setIsModalOpen(false);
    onClose?.();
  };

  return (
    <>
      <div
        onClick={handleOpen}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
        role="button"
        tabIndex={0}
      >
        <WelcomeDigitalDiscountMessage useTagShapeMobile={useTagShapeMobile} />
      </div>
      {isModalOpen && (
        <ModalWithTheme
          borderRadiusBigger
          responsiveSize="S"
          onCloseModal={handleCloseByX}
          footer={
            <Spacing vertical textAlign="center">
              <Button
                text={t('welcome_digital_discount_modal.understood')}
                variant="primary"
                onClick={handleCloseByButton}
                fullWidth
                borderRadius="M"
              />
            </Spacing>
          }
        >
          <WelcomeDigitalDiscountModalContent
            onMountTrack={() => {
              fingerprintService.markModalSeen();
              trackEvent(WELCOME_DIGITAL_DISCOUNT_MODAL_VIEWED, { screen });
            }}
          >
            <div className="overflow-y-auto">
              <Spacing vertical size="L" responsiveSize="M">
                <Spacing vertical size="M" textAlign="center">
                  <Text elementType="p" size="XXL" mobileSize="XXL" color="grayDark">
                    <strong>{t('welcome_digital_discount_modal.headline')}</strong>
                  </Text>
                  <Text elementType="p" size="M" mobileSize="S" color="grayMedium">
                    <Trans
                      t={t}
                      i18nKey="welcome_digital_discount_modal.description"
                      components={{
                        strong: <strong />,
                      }}
                    />
                  </Text>
                </Spacing>

                <Spacing vertical size="L">
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    <DiscountOptionCard
                      imageSrc={singleDiscount}
                      badgeClassName="bg-primary-regular"
                      label={t('welcome_digital_discount_modal.one_way')}
                    />

                    <Text elementType="p" size="XXL" mobileSize="XXL" color="primary">
                      <strong>+</strong>
                    </Text>

                    <DiscountOptionCard
                      imageSrc={roundTripDiscount}
                      badgeClassName="bg-accent-regular"
                      label={t('welcome_digital_discount_modal.return')}
                    />
                  </div>
                </Spacing>
                <Text elementType="p" size="S" mobileSize="XS" color="grayMedium">
                  {t('welcome_digital_discount_modal.price_note')}
                </Text>
              </Spacing>
            </div>
          </WelcomeDigitalDiscountModalContent>
        </ModalWithTheme>
      )}
    </>
  );
};

/**
 * Tracks modal shown when content mounts (modal opened via trigger click).
 */
const WelcomeDigitalDiscountModalContent = ({ children, onMountTrack }) => {
  useEffect(() => {
    onMountTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return children;
};

WelcomeDigitalDiscountModalContent.propTypes = {
  children: PropTypes.node.isRequired,
  onMountTrack: PropTypes.func.isRequired,
};

WelcomeDigitalDiscountModal.propTypes = {
  onClose: PropTypes.func,
  screen: PropTypes.oneOf(['results', 'checkout', 'passengers']).isRequired,
  useTagShapeMobile: PropTypes.bool,
};

WelcomeDigitalDiscountModal.defaultProps = {
  onClose: () => {},
  useTagShapeMobile: false,
};

export default WelcomeDigitalDiscountModal;
